import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = () => {
    setDropdownOpen(false);
    setMobileOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-background-200/60">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group cursor-pointer">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">
              Hipobuy <span className="text-primary-500">Sheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <span>Browse Categories</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 rounded-lg border border-background-200 shadow-lg py-2 max-h-96 overflow-y-auto z-50">
                  <p className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">14 Product Categories</p>
                  <div className="h-px bg-background-200/60 mx-4 mb-1"></div>
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={handleCategoryClick}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors duration-150 cursor-pointer"
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                        <i className={`${cat.icon} text-sm`}></i>
                      </div>
                      <span className="flex-1">{cat.name}</span>
                      <i className="ri-external-link-line text-xs text-foreground-400"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/hipobuy-spreadsheet-guide"
              className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Beginner&apos;s Guide
            </Link>

            <Link
              to="/spreadsheet-platform-comparison"
              className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Platform Comparison
            </Link>

            <Link
              to="/spreadsheet-category-guide"
              className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Category Guide
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              hipobuy sheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 cursor-pointer"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-6 border-t border-background-200/60 pt-4 max-h-[calc(100vh-64px)] overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 px-3 pb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-grid-line text-sm"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">Product Categories</span>
                  <span className="text-xs text-foreground-400 ml-auto">{categories.length} categories</span>
                </div>
                <div className="h-px bg-background-200/60 mb-2"></div>
                <div className="grid grid-cols-1 gap-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={handleCategoryClick}
                      className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                        <i className={`${cat.icon} text-sm`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-foreground-800">{cat.name}</span>
                        <p className="text-xs text-foreground-400 truncate mt-0.5">{cat.description.split('.')[0]}</p>
                      </div>
                      <i className="ri-arrow-right-s-line text-foreground-300 shrink-0"></i>
                    </a>
                  ))}
                </div>
              </div>

              <div className="pt-2 border-t border-background-200/60">
                <p className="px-3 pb-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">SEO Guides</p>
                <Link
                  to="/hipobuy-spreadsheet-guide"
                  onClick={handleCategoryClick}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-book-open-line text-foreground-400"></i>
                  Beginner&apos;s Guide
                </Link>
                <Link
                  to="/spreadsheet-platform-comparison"
                  onClick={handleCategoryClick}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-scales-line text-foreground-400"></i>
                  Platform Comparison
                </Link>
                <Link
                  to="/spreadsheet-category-guide"
                  onClick={handleCategoryClick}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-grid-line text-foreground-400"></i>
                  Category Guide
                </Link>
                <Link
                  to="/safe-shopping-guide"
                  onClick={handleCategoryClick}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-shield-check-line text-foreground-400"></i>
                  Safety Guide
                </Link>
              </div>

              <div className="pt-2 border-t border-background-200/60 space-y-2">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={handleCategoryClick}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-external-link-line text-foreground-400"></i>
                  hipobuy sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-full transition-all duration-200 cursor-pointer"
                >
                  Buy It Now
                  <i className="ri-shopping-bag-line text-base"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}